import { Button } from '@/components/UI/Button'
import { FileButton as MantineFileButton } from '@mantine/core'
import { IconPaperclip } from '@tabler/icons-react'
import type React from 'react'
import { useRef } from 'react'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type FileButtonProps = {
  /** ラベル文字列 */
  label: string
  /** 必須表示 */
  required?: boolean
  /** accept 属性（例: 'image/*', '.pdf,.doc'） */
  accept?: string
  /** 複数ファイル選択を許可するか */
  multiple?: boolean
  /** 選択済みファイル名の配列 */
  value?: string[]
  /** ファイル変更時コールバック（選択済みファイル名の配列を返す） */
  onChange?: (fileNames: string[]) => void
  /** エラーメッセージ */
  error?: string
  /** 非活性 */
  disabled?: boolean
  /** 選択ボタンのラベル（デフォルト: 'ファイルを選択'） */
  buttonLabel?: string
  /** ファイル未選択時のプレースホルダーテキスト（デフォルト: 'ファイルが選択されていません'） */
  noFileText?: string
  /** クリアボタンのラベル（デフォルト: 'クリア'） */
  clearLabel?: string
  style?: React.CSSProperties
  className?: string
}

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
export const FileButton = ({
  label,
  required,
  accept,
  multiple = false,
  value = [],
  onChange,
  error,
  disabled,
  buttonLabel = 'ファイルを選択',
  noFileText = 'ファイルが選択されていません',
  clearLabel = 'クリア',
  style,
  className,
}: FileButtonProps) => {
  const resetRef = useRef<(() => void) | null>(null)

  const handleChange = (files: File | File[] | null) => {
    if (files === null) {
      onChange?.([])
      return
    }
    const fileArray = Array.isArray(files) ? files : [files]
    onChange?.(fileArray.map((f) => f.name))
  }

  const handleReset = () => {
    onChange?.([])
    resetRef.current?.()
  }

  const hasFiles = value.length > 0

  return (
    <div style={style} className={className}>
      {/* ラベル */}
      <div className="text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>

      {/* ボタン + ファイル名表示 */}
      <div className="flex flex-col gap-2">
        {multiple ? (
          <MantineFileButton
            onChange={handleChange as (payload: File[]) => void}
            accept={accept}
            multiple
            resetRef={resetRef}
            disabled={disabled}
          >
            {(props) => (
              <Button
                label={buttonLabel}
                variant="primary"
                size="sm"
                leftSection={<IconPaperclip size={16} />}
                disabled={disabled}
                fullWidth
                onClick={props.onClick}
              />
            )}
          </MantineFileButton>
        ) : (
          <MantineFileButton
            onChange={handleChange as (payload: File | null) => void}
            accept={accept}
            resetRef={resetRef}
            disabled={disabled}
          >
            {(props) => (
              <Button
                label={buttonLabel}
                variant="primary"
                size="sm"
                leftSection={<IconPaperclip size={16} />}
                disabled={disabled}
                fullWidth
                onClick={props.onClick}
              />
            )}
          </MantineFileButton>
        )}

        {/* 選択済みファイル名 */}
        <div className="flex flex-col gap-1 justify-center">
          {hasFiles ? (
            value.map((name, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: ファイル名は重複の可能性があるため index も key に含める
              <span key={`${name}-${index}`} className="text-sm text-gray-700 break-all">
                {name}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400">{noFileText}</span>
          )}
        </div>
      </div>

      {/* リセットボタン */}
      {hasFiles && !disabled && (
        <button
          type="button"
          onClick={handleReset}
          className="mt-1 text-xs text-gray-400 underline hover:text-gray-600"
        >
          {clearLabel}
        </button>
      )}

      {/* エラーメッセージ */}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
